import type { FeatureToggleJson } from 'app/types/entities'
import { escapeLucene } from 'app/lib/inboxSearch'
import { getToggleMetadata, type ToggleCategory, type ToggleMetadata } from './featureToggleMetadata'

export type TargetKind = `Workspace` | `User`

export type TargetRef = { kind: TargetKind; id: string; name: string }

// One row in the By-Feature view: a known toggle plus all its persisted records.
export type FeatureRow = {
  name: string
  meta: ToggleMetadata | undefined
  label: string // meta.label ?? name (never blank)
  description: string | undefined
  global: FeatureToggleJson | undefined
  workspaceOverrides: FeatureToggleJson[]
  userOverrides: FeatureToggleJson[]
  state: `global` | `overrides` | `off`
  isKnown: boolean // false => surfaced under "Unrecognized"
  archived: boolean
}

// Per-target state for one toggle in the By-Target view.
export type TargetToggleState =
  | { kind: `global`; record: FeatureToggleJson } // inherited, switch disabled
  | { kind: `override`; record: FeatureToggleJson } // explicit row for this target
  | { kind: `off` }

export type TargetRow = {
  name: string
  meta: ToggleMetadata | undefined
  label: string
  description: string | undefined
  state: TargetToggleState
}

const UNRECOGNIZED = `Unrecognized`
export const ARCHIVED_CATEGORY = `Archived`

// Build one FeatureRow per known name, plus rows for any persisted names not in
// the known list (surfaced, never hidden). `records` are all persisted DB rows.
export function buildFeatureRows(
  records: FeatureToggleJson[],
  knownNames: readonly string[],
  definitions?: Map<string, ToggleMetadata>,
  archivedNames?: ReadonlySet<string>,
): FeatureRow[] {
  const byName = new Map<string, FeatureToggleJson[]>()
  for (const r of records) {
    if (!r.name) continue
    const list = byName.get(r.name) ?? []
    list.push(r)
    byName.set(r.name, list)
  }

  const knownSet = new Set(knownNames)
  const allNames = [...new Set<string>([...knownNames, ...byName.keys(), ...(definitions?.keys() ?? [])])]

  return allNames.map((name) => {
    const rows = byName.get(name) ?? []
    const global = rows.find((r) => r.type === `Global`)
    const workspaceOverrides = rows.filter((r) => r.type === `Workspace`)
    const userOverrides = rows.filter((r) => r.type === `User`)
    const state: FeatureRow[`state`] = global
      ? `global`
      : workspaceOverrides.length || userOverrides.length
        ? `overrides`
        : `off`
    const meta = definitions?.get(name) ?? getToggleMetadata(name)
    const recordDescription = global?.description ?? workspaceOverrides[0]?.description ?? userOverrides[0]?.description
    return {
      name,
      meta,
      label: meta?.label ?? name,
      description: meta?.description ?? recordDescription,
      global,
      workspaceOverrides,
      userOverrides,
      state,
      isKnown: knownSet.has(name) || (definitions?.has(name) ?? false),
      archived: archivedNames?.has(name) ?? false,
    }
  })
}

// Group rows by category for display. Unknown rows collapse under "Unrecognized",
// and archived rows, known or not, under "Archived" after it.
const CATEGORY_ORDER: ToggleCategory[] = [
  `Contacts`,
  `Analytics`,
  `Scheduling`,
  `Communications`,
  `Integrations`,
  `Platform`,
  `Internal`,
]

export function groupFeatureRowsByCategory(rows: FeatureRow[]): Array<{ category: string; rows: FeatureRow[] }> {
  const groups = new Map<string, FeatureRow[]>()
  for (const row of rows) {
    const category = row.archived ? ARCHIVED_CATEGORY : row.isKnown ? (row.meta?.category ?? `Internal`) : UNRECOGNIZED
    const list = groups.get(category) ?? []
    list.push(row)
    groups.set(category, list)
  }
  const orderIndex = (c: string) => {
    if (c === ARCHIVED_CATEGORY) return CATEGORY_ORDER.length + 2
    if (c === UNRECOGNIZED) return CATEGORY_ORDER.length + 1
    const i = CATEGORY_ORDER.indexOf(c as ToggleCategory)
    return i === -1 ? CATEGORY_ORDER.length : i
  }
  return [...groups.entries()]
    .sort((a, b) => orderIndex(a[0]) - orderIndex(b[0]))
    .map(([category, rs]) => ({
      category,
      rows: [...rs].sort((a, b) => a.label.localeCompare(b.label)),
    }))
}

/**
 * Search string for the `users` / `workspaces` pickers.
 *
 * Both queries hand the raw string to an Elasticsearch query_string, which
 * matches whole tokens: a bare `trev` scores zero against "Trevor Hayes", and
 * `eag` zero against "Eagle Family Dental". Every term the person types has to
 * be wildcarded into a named field, and AND-ed so "trevor hayes" narrows.
 * Returns undefined for an empty query, which lists the first page unfiltered.
 * `query` is optional because ComboAsync's prefetch calls its fetcher with no
 * search text at all, despite typing the parameter as a string.
 *
 * `internalOnly` narrows users to Trackable staff: anyone with a trackable.io
 * email. The API still drops org and super users from this list while the
 * caller sits in a workspace, so until that is lifted it finds only the
 * internal users of the current workspace.
 */
export function buildTargetSearch(
  kind: TargetKind,
  query: string | undefined,
  internalOnly = false,
): string | undefined {
  const trimmed = query?.trim()
  const internal = internalOnly && kind === `User`
  if (!trimmed) return internal ? INTERNAL_USERS_SEARCH : undefined
  const terms = trimmed.split(/\s+/).map((term) => `*${escapeLucene(term)}*`)
  const group = `(${terms.join(` AND `)})`
  if (kind === `Workspace`) return `name:${group}`
  const clauses = [`name:${group}`, `email:${group}`]
  // A whole address ("trevor.hayes@trackable.io") is one analyzer token, so the
  // wildcard form misses it; the phrase clause is what actually matches it.
  if (trimmed.includes(`@`)) clauses.push(`email:"${escapeLucene(trimmed)}"`)
  const text = clauses.join(` OR `)
  return internal ? `${INTERNAL_USERS_SEARCH} AND (${text})` : text
}

export const INTERNAL_USERS_SEARCH = `email:"trackable.io"`

// For a chosen target, compute each known, unarchived toggle's state.
export function buildTargetRows(
  records: FeatureToggleJson[],
  knownNames: readonly string[],
  target: TargetRef,
  definitions?: Map<string, ToggleMetadata>,
  archivedNames?: ReadonlySet<string>,
): TargetRow[] {
  return knownNames
    .filter((name) => !archivedNames?.has(name))
    .map((name) => {
      const rows = records.filter((r) => r.name === name)
      const global = rows.find((r) => r.type === `Global`)
      const override = rows.find((r) => r.type === target.kind && r.typeId === target.id)
      const meta = definitions?.get(name) ?? getToggleMetadata(name)
      const description = meta?.description ?? rows[0]?.description
      let state: TargetToggleState
      if (global) state = { kind: `global`, record: global }
      else if (override) state = { kind: `override`, record: override }
      else state = { kind: `off` }
      return { name, meta, label: meta?.label ?? name, description, state }
    })
}
